/**
 * WalletPayButton — One-tap Apple Pay / Google Pay button using
 * Stripe's Payment Request API (bypasses hosted Checkout).
 *
 * Feb 12 2026 user spec: *"Apple/Google Pay buttons directly on the
 * in-app pricing page (custom Payment Request API surface — bypasses
 * hosted Checkout for one-tap pay)."*
 *
 * Behaviour:
 *   - Lazy-loads Stripe.js with the publishable key fetched from
 *     `/api/payments/stripe/publishable-key`.
 *   - If the key isn't set OR the device can't make a wallet payment,
 *     the component renders nothing (callers can fall back to the
 *     gateway picker silently).
 *   - On tap, it shows the native wallet sheet, then confirms the
 *     PaymentIntent in-place — no redirect.
 *
 * Props:
 *   packageId (string)  — which fixed package on the server to charge for
 *   amount    (number)  — amount in MINOR units (pence/cents) for display
 *   currency  (string)  — 'gbp' | 'usd' | ...
 *   label     (string)  — display label shown in the wallet sheet
 *   onSuccess (fn)      — called with the final paymentIntent on success
 *   onError   (fn)      — called with the error on failure
 */
import React, { useEffect, useState, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js';
import { authFetch } from '../utils/authUtils';
import { PAYMENTS_DISABLED } from '../utils/paymentAvailability';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Cache the Stripe instance across renders so we only load the SDK once.
let _stripePromise = null;
const getStripe = async () => {
  if (_stripePromise) return _stripePromise;
  const r = await fetch(`${BACKEND_URL}/api/payments/stripe/publishable-key`);
  const d = await r.json().catch(() => ({}));
  if (!d.key) return null;
  _stripePromise = loadStripe(d.key);
  return _stripePromise;
};

// Inner element that actually mounts the PaymentRequestButton.
const InnerButton = ({ packageId, amount, currency, label, onSuccess, onError }) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canPay, setCanPay] = useState(false);
  const submittedRef = useRef(false);

  useEffect(() => {
    if (!stripe) return;
    const pr = stripe.paymentRequest({
      country: 'GB',
      currency: (currency || 'gbp').toLowerCase(),
      total: {
        label: label || 'Smarter Learning Hub',
        amount: Number(amount) || 0,
      },
      requestPayerName: true,
      requestPayerEmail: true,
      disableWallets: [],
    });

    pr.canMakePayment().then((result) => {
      if (result) {
        setPaymentRequest(pr);
        setCanPay(true);
      } else {
        setCanPay(false);
      }
    });

    pr.on('paymentmethod', async (ev) => {
      if (submittedRef.current) return;
      submittedRef.current = true;
      try {
        // Step 1 — create a PaymentIntent on the server.
        const r = await authFetch(`${BACKEND_URL}/api/payments/stripe/payment-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ package_id: packageId }),
        });
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          ev.complete('fail');
          onError?.(d.detail || 'Could not start payment');
          return;
        }
        const { client_secret } = await r.json();

        // Step 2 — confirm with the payment method from the wallet.
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          client_secret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false },
        );
        if (error) {
          ev.complete('fail');
          onError?.(error.message || 'Payment failed');
          submittedRef.current = false;
          return;
        }
        ev.complete('success');

        // Step 3 — if 3DS or another action is required, handle it.
        if (paymentIntent.status === 'requires_action') {
          const r2 = await stripe.confirmCardPayment(client_secret);
          if (r2.error) {
            onError?.(r2.error.message || 'Authentication failed');
            return;
          }
        }

        // Step 4 — sync the final status back to the server.
        await authFetch(
          `${BACKEND_URL}/api/payments/stripe/payment-intent/${paymentIntent.id}/sync`,
          { method: 'POST' },
        );
        onSuccess?.(paymentIntent);
      } catch (e) {
        ev.complete('fail');
        onError?.(e?.message || 'Payment failed');
        submittedRef.current = false;
      }
    });
  }, [stripe, packageId, amount, currency, label, onSuccess, onError]);

  if (!paymentRequest || !canPay) return null;
  return (
    <div data-testid="wallet-pay-button" className="my-3">
      <PaymentRequestButtonElement
        options={{
          paymentRequest,
          style: {
            paymentRequestButton: {
              type: 'default',
              theme: 'dark',
              height: '48px',
            },
          },
        }}
      />
      <p className="text-[10px] text-center text-white/40 uppercase tracking-widest mt-1">
        Tap to pay · Apple Pay · Google Pay
      </p>
    </div>
  );
};

const WalletPayButton = (props) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (PAYMENTS_DISABLED) {
      setHydrated(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const p = await getStripe();
      if (!cancelled) {
        setStripePromise(p);
        setHydrated(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (PAYMENTS_DISABLED) return null;
  if (!hydrated) return null;
  if (!stripePromise) return null;

  return (
    <Elements stripe={stripePromise}>
      <InnerButton {...props} />
    </Elements>
  );
};

export default WalletPayButton;
